import React from 'react';
import { Template, TemplateElement } from './types';

export interface TemplatePreviewProps {
  template: Template;
  isOpen: boolean;
  onClose: () => void;
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ template, isOpen, onClose }) => {
  if (!isOpen) return null;

  const getElementIcon = (type: string) => {
    switch (type) {
      case 'question': return '❓';
      case 'statement': return '💬';
      case 'checklist': return '✅';
      default: return '💭';
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      opening: 'bg-green-100 text-green-800 border-green-200',
      discovery: 'bg-blue-100 text-blue-800 border-blue-200',
      presentation: 'bg-purple-100 text-purple-800 border-purple-200',
      closing: 'bg-red-100 text-red-800 border-red-200',
      'follow-up': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[category.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getDifficultyColor = (difficulty?: string) => {
    const colors: Record<string, string> = {
      beginner: 'bg-green-50 text-green-700 border-green-200',
      intermediate: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      advanced: 'bg-red-50 text-red-700 border-red-200'
    };
    return colors[difficulty || 'beginner'] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getDifficultyLabel = (difficulty?: string) => {
    const labels: Record<string, string> = {
      beginner: 'Standard',
      intermediate: 'Intermediate',
      advanced: 'Advanced'
    };
    return labels[difficulty || 'beginner'] || difficulty;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">{template.name}</h2>
              <p className="text-blue-100 mt-1">{template.description}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 transition-colors p-2"
              title="Close Preview"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          {/* Template Metadata */}
          <div className="flex items-center space-x-4 mt-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getDifficultyColor(template.metadata.difficulty)}`}>
              {getDifficultyLabel(template.metadata.difficulty)}
            </span>
            <span className="text-blue-100">
              📂 {template.category}
            </span>
            {template.metadata.estimatedDuration && (
              <span className="text-blue-100">
                ⏱️ {template.metadata.estimatedDuration}
              </span>
            )}
            <span className="text-blue-100">
              📝 {template.elements.length} element{template.elements.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div 
          className="flex-1 overflow-y-scroll min-h-0 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100" 
          style={{ 
            maxHeight: 'calc(-140px + 80vh)', 
            scrollbarWidth: 'thin', 
            scrollbarColor: 'rgb(209, 213, 219) rgb(243, 244, 246)' 
          }}
        >
          <div className="p-4">
            {template.elements.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <div className="text-6xl mb-4">📝</div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No Elements Yet</h3>
                <p className="text-sm">Add your first question or statement to see the preview!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {template.elements.map((element, index) => (
                  <div key={element.id} className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                    {/* Element Header */}
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-semibold text-gray-700">#{element.order}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getCategoryColor(element.category)}`}>
                          {element.category}
                        </span>
                        {element.required && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full border border-red-200">
                            Required
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 capitalize">
                        {element.type}
                      </span>
                    </div>

                    {/* Element Content - Clean and Simple */}
                    <div>
                      <p className="text-gray-900 font-medium leading-relaxed mb-1">{element.content}</p>
                      
                      {element.placeholder && (
                        <p className="text-blue-600 text-sm italic">"{element.placeholder}"</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="flex-shrink-0 border-t border-gray-200 p-4 bg-gray-50 rounded-b-lg">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Created: {new Date(template.createdAt).toLocaleDateString()} • 
              Updated: {new Date(template.updatedAt).toLocaleDateString()}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close Preview
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreview; 